'use client';

import React, { Children, useCallback, useLayoutEffect, useRef } from 'react';
import { useStickyNavOffset } from '@benzinga/hooks';

const HOLD_PX = 1000;
const DESKTOP_MIN_WIDTH_PX = 800;

interface StickyUnit {
  stage: HTMLElement;
  element: HTMLElement;
  contentOffset: number;
  height: number;
  stickEnd: number;
  hideAt: number;
}

interface StickyMetrics {
  active: boolean;
  pin: number;
  gap: number;
  stackDocumentTop: number;
  units: StickyUnit[];
  leaderIndex: number;
  exitShift: number;
  signature: string;
}

const idleMetrics = (): StickyMetrics => ({
  active: false,
  pin: 0,
  gap: 0,
  stackDocumentTop: 0,
  units: [],
  leaderIndex: -1,
  exitShift: 0,
  signature: '',
});

export const SequentialStickyStack: React.FC<React.PropsWithChildren<{ enabled: boolean }>> = ({
  children,
  enabled,
}) => {
  const navOffset = useStickyNavOffset();
  const stackRef = useRef<HTMLDivElement>(null);
  const metricsRef = useRef<StickyMetrics>(idleMetrics());
  const childList = Children.toArray(children) as React.ReactElement[];

  const paint = useCallback((scrollPastPin: number) => {
    const metrics = metricsRef.current;
    const { pin, units } = metrics;
    if (!metrics.active || units.length === 0) return;

    const found = units.findIndex(unit => scrollPastPin < unit.hideAt);
    const leaderIndex = found < 0 ? units.length - 1 : found;
    const leader = units[leaderIndex];
    const exiting = Number.isFinite(leader.hideAt) && scrollPastPin > leader.stickEnd;
    const exitShift = exiting ? Math.min(scrollPastPin - leader.stickEnd, leader.height + metrics.gap) : 0;

    if (!exiting && leaderIndex === metrics.leaderIndex && metrics.exitShift === 0) return;

    const leaderChanged = leaderIndex !== metrics.leaderIndex;
    units.forEach((unit, index) => {
      const released = index < leaderIndex;
      if (leaderChanged) {
        unit.element.style.visibility = released ? 'hidden' : '';
        unit.element.style.pointerEvents = released ? 'none' : '';
        unit.element.style.zIndex = index === leaderIndex ? '2' : '1';
        unit.element.style.top = `${pin + unit.contentOffset - leader.contentOffset}px`;
      }
      const nextTransform = !released && exitShift > 0 ? `translate3d(0, ${-exitShift}px, 0)` : '';
      if (unit.element.style.transform !== nextTransform) unit.element.style.transform = nextTransform;
    });

    metrics.leaderIndex = leaderIndex;
    metrics.exitShift = exitShift;
  }, []);

  const measure = useCallback(() => {
    const stack = stackRef.current;
    if (!stack) return;

    const stages = [...stack.children] as HTMLElement[];
    const clearStick = () => {
      stages.forEach(stage => {
        stage.style.height = '';
        stage.style.marginBottom = '';
        const unit = stage.firstElementChild as HTMLElement | null;
        if (!unit) return;
        unit.style.top = '';
        unit.style.visibility = '';
        unit.style.pointerEvents = '';
        unit.style.zIndex = '';
        unit.style.transform = '';
      });
    };

    if (!enabled || window.innerWidth < DESKTOP_MIN_WIDTH_PX) {
      clearStick();
      metricsRef.current = idleMetrics();
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(stack).rowGap) || 0;
    const measured: StickyUnit[] = [];
    stages.forEach(stage => {
      const element = stage.firstElementChild as HTMLElement | null;
      if (!element || element.offsetHeight <= 0) {
        stage.style.display = 'none';
        return;
      }
      stage.style.display = '';
      measured.push({
        stage,
        element,
        contentOffset: 0,
        height: element.offsetHeight,
        stickEnd: 0,
        hideAt: 0,
      });
    });

    if (measured.length === 0) {
      clearStick();
      metricsRef.current = idleMetrics();
      return;
    }

    const rail = stack.closest('.layout-sidebar');
    const railHeight = rail ? rail.getBoundingClientRect().height : 0;
    const pin = navOffset + gap;
    const signature = `${pin}|${Math.round(railHeight)}|${measured.map(unit => Math.round(unit.height)).join(',')}`;
    if (signature === metricsRef.current.signature) {
      metricsRef.current.stackDocumentTop = stack.getBoundingClientRect().top + window.scrollY;
      paint(Math.max(0, pin - (metricsRef.current.stackDocumentTop - window.scrollY)));
      return;
    }

    let stackedOffset = 0;
    let scrollCursor = 0;
    measured.forEach(unit => {
      const last = unit === measured[measured.length - 1];
      unit.contentOffset = stackedOffset;
      unit.stickEnd = last ? Number.POSITIVE_INFINITY : scrollCursor + HOLD_PX;
      unit.hideAt = last ? Number.POSITIVE_INFINITY : unit.stickEnd + unit.height + gap;
      scrollCursor = last ? scrollCursor : unit.hideAt;
      stackedOffset += unit.height + gap;
    });
    measured.forEach(unit => {
      const last = unit.hideAt === Number.POSITIVE_INFINITY;
      const stageHeight = Math.max(unit.height, (railHeight || stackedOffset) - unit.contentOffset);
      unit.stage.style.height = `${stageHeight}px`;
      unit.stage.style.marginBottom = last ? '0px' : `${unit.height - stageHeight}px`;
    });

    metricsRef.current = {
      active: true,
      pin,
      gap,
      stackDocumentTop: stack.getBoundingClientRect().top + window.scrollY,
      units: measured,
      leaderIndex: -1,
      exitShift: 0,
      signature,
    };
    const scrollPastPin = Math.max(0, metricsRef.current.pin - (metricsRef.current.stackDocumentTop - window.scrollY));
    paint(scrollPastPin);
  }, [enabled, navOffset, paint]);

  useLayoutEffect(() => {
    const stack = stackRef.current;
    if (!stack) return;

    measure();
    const onScroll = () => {
      const metrics = metricsRef.current;
      if (!metrics.active) return;
      paint(Math.max(0, metrics.pin - (metrics.stackDocumentTop - window.scrollY)));
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
    const observer = new ResizeObserver(() => measure());
    observer.observe(stack);
    const rail = stack.closest('.layout-sidebar');
    if (rail) observer.observe(rail);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [measure, paint]);

  return (
    <div className="relative flex h-full flex-col gap-4" ref={stackRef}>
      {childList.map(child => (
        <div key={child.key ?? undefined}>
          <div className={enabled ? 'sticky self-start max-[799px]:static' : undefined}>{child}</div>
        </div>
      ))}
    </div>
  );
};
